/* EntryRow — LED-01, LED-04, LED-05, LED-06, LED-07, LED-08 */
import { Amount } from '../primitives/Amount';
import { Row } from '../primitives/Row';
import { Skeleton } from '../primitives/Skeleton';
import styles from './EntryRow.module.css';

export interface EntryRowProps {
  /** The purpose's emoji. Every row on every ledger screen has one (`FEAT-010 §3.1`). */
  readonly icon: string;
  /** The note, or the purpose when there is no note — whatever the person would recognise. */
  readonly title: string;
  /** "Grocery · Today", "Freelance · 15 Aug". The purpose and when. */
  readonly detail: string;
  /** Paise, always positive. */
  readonly amountMinor: number;
  /** Which way the money went. The only thing that decides the sign and the colour. */
  readonly kind: 'debit' | 'credit';
  /** Opens `LED-04`. Absent makes the row a plain line rather than a button. */
  readonly onPress?: () => void;
  /** Renders greyed, for `LED-07` where an entry is in the trash. */
  readonly muted?: boolean;
  /** Shown on the right instead of the amount — `LED-07`'s "1 day left". */
  readonly trailing?: React.ReactNode;
}

/**
 * One entry, on every screen that lists one (`LED-01`, `LED-05`, `LED-06`, `LED-07`).
 *
 * **The sign is derived from `kind` and never passed in.** That is the component's job: `FEAT-011 §3.2`
 * says the direction lives in `Kind` and nowhere else, and a row that took a pre-signed number would be a
 * second place it lived — one that could disagree with the first the day somebody formatted an amount by
 * hand. The amount arrives positive and leaves with a `−` or a `+` in front of it.
 *
 * The title is one line and truncates. `LED-05` fits eight rows on a phone, and a note that wrapped to
 * three lines would push the running balance off the screen it is there to sit beside.
 */
export function EntryRow({
  icon,
  title,
  detail,
  amountMinor,
  kind,
  onPress,
  muted = false,
  trailing,
}: EntryRowProps): React.JSX.Element {
  const content = (
    <span className={styles.line}>
      <span
        className={[styles.icon, kind === 'credit' ? styles.credit : styles.debit, muted ? styles.dim : '']
          .filter(Boolean)
          .join(' ')}
        aria-hidden="true"
      >
        {icon}
      </span>
      <span className={styles.text}>
        <span className={[styles.title, muted ? styles.dim : ''].filter(Boolean).join(' ')}>{title}</span>
        <span className={styles.detail}>{detail}</span>
      </span>
    </span>
  );

  // The sign is applied here and comes from `kind` alone. A caller passes a positive amount, always —
  // taking a pre-signed number would put the direction in two places, and two places disagree.
  const right = trailing ?? (
    <Amount amountMinor={kind === 'credit' ? amountMinor : -amountMinor} tone={kind} format={{ signed: true }} />
  );

  return onPress ? (
    <Row onPress={onPress} trailing={right}>
      {content}
    </Row>
  ) : (
    <Row trailing={right}>{content}</Row>
  );
}

/**
 * The shape of a row that has not arrived (`STD-002 §3.4`).
 *
 * Exported beside the row rather than left to each screen, so a list that is loading looks the same
 * everywhere and is the same height as the list that replaces it.
 */
export function EntryRowSkeleton({ count = 1 }: { readonly count?: number }): React.JSX.Element {
  return (
    <div data-testid="entry-row-skeleton">
      {Array.from({ length: count }, (_, index) => (
        <Row key={index} trailing={<Skeleton width="4rem" height="1rem" />}>
          <span className={styles.line}>
            <Skeleton width="34px" height="34px" />
            <span className={styles.text}>
              <Skeleton width="9rem" height="0.9rem" />
              <Skeleton width="5rem" height="0.75rem" />
            </span>
          </span>
        </Row>
      ))}
    </div>
  );
}
